import { useCallback, useEffect, useMemo, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import IconButton from 'src/components/IconButton';
import Iconify from 'src/components/Iconify';
import InfoAlert from 'src/components/InfoAlert';
import ListActionBar from 'src/components/ListActionBar';
import TableContainer from 'src/components/Table/TableContainer';
import LoadingRows from 'src/components/Table/LoadingRows';
import NoDataCell from 'src/components/NoDataCell';
import { readFilterParams, useFilterParams } from 'src/hooks/filterParams';
import {
  useInfiniteBasePolicyGroups,
  useTogglePolicyHidden,
} from 'src/hooks/environmentWdacPolicy';
import { pagesText } from 'src/constants/pagesText';
import { PORTAL_ROUTE } from 'src/constants/routes';
import { getBasePolicyModeOptions } from 'src/utils/enums/basePolicyMode';
import {
  POLICY_VISIBILITY,
  getPolicyVisibilityOptions,
  toIsHiddenValues,
} from 'src/utils/enums/uiOnly/policyVisibility';
import {
  POLICY_TABLE_SX,
  PolicyActionsCell,
  PolicyCaptionCell,
  PolicyHeaderCell,
  PolicyManagedCell,
  PolicyNameCell,
  PolicyTypeCell,
  toBooleanParam,
} from './PolicyTableShared';
import usePolicyManagedAction from './usePolicyManagedAction';

const PAGE_LIMIT = 50;

const TABLE_MAX_HEIGHT = 'calc(100vh - 320px)';

// The bases worth attention are the ones actually running on the fleet, so the groups lead with the
// widest deployed rather than alphabetically.
const DEFAULT_SORT = { column: 'Devices', direction: 'desc' };

const DEFAULT_FILTERS = {
  visibilities: [POLICY_VISIBILITY.Visible],
  basePolicyMode: '',
  isPortalManaged: '',
};

// Every top-level row is a base policy, so there is no type filter and mode is never disabled.
const FILTER_OPTIONS = [
  {
    key: 'visibilities',
    type: 'checkbox',
    label: 'Visibility',
    options: getPolicyVisibilityOptions(),
  },
  {
    key: 'basePolicyMode',
    type: 'select',
    label: 'Base Policy Mode',
    options: [{ value: '', label: 'All modes' }, ...getBasePolicyModeOptions()],
  },
  {
    key: 'isPortalManaged',
    type: 'select',
    label: 'Managed',
    options: [
      { value: '', label: 'All policies' },
      { value: 'true', label: 'Managed' },
      { value: 'false', label: 'Not managed' },
    ],
  },
];

// Sorting applies to the base policies; supplementals sit under their base in device order.
const GROUP_COLUMNS = [
  { key: 'actions', label: 'Actions', width: '120px' },
  { key: 'policyName', label: 'Policy Name', sortKey: 'PolicyName', width: '30%' },
  { key: 'policyVersion', label: 'Policy Version', sortKey: 'PolicyVersion', width: '14%' },
  { key: 'policyType', label: 'Policy Type', width: '18%' },
  { key: 'managed', label: 'Managed', sortKey: 'IsPortalManaged', width: '16%' },
  { key: 'devices', label: 'Devices', sortKey: 'Devices', width: '100px' },
  { key: 'expand', label: 'Related', width: '108px' },
];

const ALERT_SX = { width: '100%', py: 0.25 };

const CHILD_ROW_SX = { backgroundColor: '#3F3F3F' };

const supplementalCountTooltip = count =>
  count
    ? `${count} supplemental ${
        count === 1 ? 'policy has' : 'policies have'
      } been detected under this base policy.`
    : 'No supplemental policies have been detected under this base policy.';

const BasePolicyGroupsTable = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [searchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useState(
    () => readFilterParams(DEFAULT_FILTERS) ?? DEFAULT_FILTERS,
  );
  const [expanded, setExpanded] = useState(() => new Set());
  const [sort, setSort] = useState(DEFAULT_SORT);

  const { onToggleManaged, managedDisabled, managedDialogs } = usePolicyManagedAction();
  const toggleHiddenMutation = useTogglePolicyHidden();

  useFilterParams(filters);

  const handleSort = useCallback(column => {
    if (!column.sortKey) {
      return;
    }

    setSort(prev =>
      prev.column === column.sortKey
        ? { column: column.sortKey, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { column: column.sortKey, direction: 'asc' },
    );
  }, []);

  // The api layer rejects with the backend's message as a plain string, and the business rules
  // behind these toggles are worth showing verbatim rather than as a generic failure.
  const showMutationError = useCallback(
    error =>
      enqueueSnackbar(typeof error === 'string' && error ? error : pagesText.somethingWentWrong, {
        variant: 'error',
        autoHideDuration: 4000,
      }),
    [enqueueSnackbar],
  );

  const handleToggleHidden = useCallback(
    async policy => {
      try {
        await toggleHiddenMutation.mutateAsync({ envPolicyId: policy.id });
        enqueueSnackbar(policy.isHidden ? 'Policy unhidden.' : 'Policy hidden.', {
          variant: 'success',
          autoHideDuration: 3000,
        });
      } catch (error) {
        showMutationError(error);
      }
    },
    [enqueueSnackbar, showMutationError, toggleHiddenMutation],
  );

  const apiQuery = useMemo(() => {
    const params = {};
    const trimmed = searchValue.trim();
    if (trimmed) {
      params.search = trimmed;
    }
    if (filters.visibilities.length) {
      params.isHidden = toIsHiddenValues(filters.visibilities);
    }
    if (filters.basePolicyMode) {
      params.basePolicyMode = filters.basePolicyMode;
    }
    params.isPortalManaged = toBooleanParam(filters.isPortalManaged);
    params.sortColumn = sort.column;
    params.sortDesc = sort.direction === 'desc';
    return params;
  }, [searchValue, filters, sort]);

  const groupsQuery = useInfiniteBasePolicyGroups({ apiQuery, limit: PAGE_LIMIT });

  const items = useMemo(
    () => groupsQuery.data?.pages?.flatMap(page => page.results || []) || [],
    [groupsQuery.data],
  );
  const totalCount = groupsQuery.data?.pages?.[0]?.totalCount || 0;

  useEffect(() => {
    setExpanded(new Set());
  }, [apiQuery]);

  const loadMore = useCallback(() => {
    if (groupsQuery.hasNextPage) {
      groupsQuery.fetchNextPage();
    }
  }, [groupsQuery]);

  const [sentryRef] = useInfiniteScroll({
    loading: groupsQuery.isFetchingNextPage,
    hasNextPage: groupsQuery.hasNextPage,
    disabled: false,
    onLoadMore: loadMore,
    rootMargin: '0px 0px 400px 0px',
  });

  const handleFilterChange = useCallback(nextFilters => {
    setFilters({
      visibilities: nextFilters.visibilities || [],
      basePolicyMode: nextFilters.basePolicyMode || '',
      isPortalManaged: nextFilters.isPortalManaged || '',
    });
  }, []);

  const handleToggleExpand = useCallback(basePolicyId => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(basePolicyId)) {
        next.delete(basePolicyId);
      } else {
        next.add(basePolicyId);
      }
      return next;
    });
  }, []);

  const handleOpenPolicy = useCallback(
    policyId => navigate(`${PORTAL_ROUTE}/policy-details/${policyId}/general`),
    [navigate],
  );

  // Supplemental rows reuse the base row's columns so a group reads as one table; the only
  // differences are the indented name and the expand control, which belongs to the base alone.
  const renderCell = useCallback(
    (column, policy, { nested = false, childCount = 0, isExpanded = false } = {}) => {
      switch (column.key) {
        case 'actions':
          return (
            <PolicyActionsCell
              key={`${policy.id}-actions`}
              policy={policy}
              hiddenDisabled={toggleHiddenMutation.isLoading}
              managedDisabled={managedDisabled}
              onToggleManaged={onToggleManaged}
              onToggleHidden={handleToggleHidden}
            />
          );
        case 'policyName':
          return (
            <PolicyNameCell
              key={`${policy.id}-name`}
              policyName={policy.policyName}
              policyId={policy.policyId}
              onOpen={() => handleOpenPolicy(policy.id)}
            />
          );
        case 'policyVersion':
          return (
            <PolicyCaptionCell key={`${policy.id}-version`}>
              {policy.policyVersion || '—'}
            </PolicyCaptionCell>
          );
        case 'policyType':
          return (
            <PolicyTypeCell
              key={`${policy.id}-type`}
              policyType={policy.policyType}
              basePolicyMode={policy.basePolicyMode}
            />
          );
        case 'devices':
          return (
            <PolicyCaptionCell key={`${policy.id}-devices`}>
              {policy.devicesCount ?? 0}
            </PolicyCaptionCell>
          );
        case 'managed':
          return <PolicyManagedCell key={`${policy.id}-managed`} policy={policy} />;
        // A base with nothing detected under it still says so rather than leaving the cell empty,
        // which is the common case until agents report what a supplemental attaches to.
        case 'expand':
          return nested ? (
            <TableCell key={`${policy.id}-expand`} />
          ) : (
            <TableCell key={`${policy.id}-expand`} sx={{ py: 0.75 }}>
              <Tooltip title={supplementalCountTooltip(childCount)}>
                <Stack direction="row" alignItems="center" gap={0.25}>
                  <Typography
                    variant="caption"
                    sx={{ color: childCount ? '#D1D5DB' : '#9CA3AF', fontWeight: 600 }}>
                    {childCount}
                  </Typography>
                  <IconButton
                    disabled={!childCount}
                    onClick={event => {
                      event.stopPropagation();
                      handleToggleExpand(policy.id);
                    }}
                    sx={{ '&:hover': { backgroundColor: 'unset' } }}>
                    <Iconify
                      icon={isExpanded ? 'mdi:chevron-up' : 'mdi:chevron-down'}
                      width={20}
                      height={20}
                      color={childCount ? '#9CA3AF' : '#5B5B5B'}
                    />
                  </IconButton>
                </Stack>
              </Tooltip>
            </TableCell>
          );
        default:
          return <TableCell key={`${policy.id}-${column.key}`} />;
      }
    },
    [
      handleOpenPolicy,
      onToggleManaged,
      handleToggleExpand,
      handleToggleHidden,
      toggleHiddenMutation.isLoading,
      managedDisabled,
    ],
  );

  return (
    <Box sx={{ position: 'relative' }}>
      <ListActionBar
        onSearch={setSearchValue}
        defaultSearch={searchValue}
        onResetSearch={() => setSearchValue('')}
        searchPlaceholder="Base policy name or GUID"
        filters={filters}
        onFilterChange={handleFilterChange}
        filterOptions={FILTER_OPTIONS}
        filterChips
        limitChips={3}
        filterButtonCount={totalCount}
        contentArea={
          <InfoAlert sx={ALERT_SX}>
            <Typography variant="caption">
              Groups are assembled from what devices report. Older agent versions do not report
              which base a supplemental policy attaches to, and devices can run different
              supplementals against the same base, so a group here may be incomplete and shows every
              supplemental detected under a base rather than any single device&apos;s set.
            </Typography>
          </InfoAlert>
        }
        contentAreaContainerProps={{ sx: { mt: 0.5 } }}
      />

      <TableContainer roundedTop styles={{ maxHeight: TABLE_MAX_HEIGHT }}>
        <Table stickyHeader size="small" sx={POLICY_TABLE_SX}>
          <colgroup>
            {GROUP_COLUMNS.map(column => (
              <col key={column.key} style={{ width: column.width }} />
            ))}
          </colgroup>
          <TableHead sx={{ backgroundColor: '#3F3F3F' }}>
            <TableRow>
              {GROUP_COLUMNS.map(column => (
                <PolicyHeaderCell
                  key={column.key}
                  column={column}
                  sort={sort}
                  onSort={handleSort}
                />
              ))}
            </TableRow>
          </TableHead>
          <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
            {groupsQuery.isLoading ? (
              <LoadingRows columnCount={GROUP_COLUMNS.length} rowCount={10} />
            ) : (
              <>
                {items.map((item, index) => {
                  const { basePolicy, supplementalPolicies = [] } = item;
                  const hasChildren = supplementalPolicies.length > 0;
                  const isExpanded = hasChildren && expanded.has(basePolicy.id);

                  return [
                    <TableRow
                      key={basePolicy.id}
                      ref={index === items.length - 1 ? sentryRef : null}
                      hover
                      onClick={hasChildren ? () => handleToggleExpand(basePolicy.id) : undefined}
                      sx={{ cursor: hasChildren ? 'pointer' : 'default' }}>
                      {GROUP_COLUMNS.map(column =>
                        renderCell(column, basePolicy, {
                          childCount: supplementalPolicies.length,
                          isExpanded,
                        }),
                      )}
                    </TableRow>,
                    ...(isExpanded
                      ? supplementalPolicies.map(supplemental => (
                          <TableRow
                            key={`${basePolicy.id}-${supplemental.id}`}
                            hover
                            onClick={() => handleOpenPolicy(supplemental.id)}
                            sx={{ ...CHILD_ROW_SX, cursor: 'pointer' }}>
                            {GROUP_COLUMNS.map(column =>
                              renderCell(column, supplemental, { nested: true }),
                            )}
                          </TableRow>
                        ))
                      : []),
                  ];
                })}
                {groupsQuery.isFetchingNextPage ? (
                  <LoadingRows columnCount={GROUP_COLUMNS.length} rowCount={3} />
                ) : null}
                {!groupsQuery.isFetchingNextPage && !items.length ? (
                  <NoDataCell colSpan={GROUP_COLUMNS.length}>
                    No base policies have been detected on devices yet.
                  </NoDataCell>
                ) : null}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {managedDialogs}
    </Box>
  );
};

export default BasePolicyGroupsTable;
